// Platform name + color resolution.
//
// Source of truth lives in the API at GET /platforms/metadata
// (apps/api/src/data/platform_metadata.py). The endpoint returns:
//
//   {
//     entries: [{ platform, id_platform, country }, ...],   // display-ready
//     colors:  { [id_platform]: "#hex", ..., DEFAULT: "#hex" },
//   }
//
// The `platform` strings are already display-ready — overrides are
// applied server-side. The frontend just renders them verbatim. No
// normalization, no override map, no precomputed lookup tables — every
// query is an Array.find against the entries list.
//
// On app boot, App.jsx calls applyPlatformMetadata(payload) with the
// API response (or with a localStorage-cached copy from a prior
// session). We deliberately keep NO hardcoded seed of platforms:
// clients on bad internet should see raw codes or nothing rather than
// platforms they don't actually have data for. The only built-in is a
// neutral DEFAULT color so unresolved codes don't render colorless.

let entries = [];
let colors = { DEFAULT: "#8B8B8B" };
const warnedUnknownInputs = new Set();

// Names resolve at render time, so data never waits on this mapping. The
// version bumps on every applied payload; a component that memoizes a
// resolved name lists `usePlatformMetadataVersion()` in its deps, so it
// re-renders when late metadata turns raw codes into names. Store raw
// `id_platform` codes in Redux, never resolved names.
let version = 0;
const listeners = new Set();

/**
 * Replace the in-memory mapping with the API payload. Called on app
 * boot — first with any localStorage-cached payload from a prior
 * session (see `apps/web/src/api/platformMetadataApi.ts`), then again
 * once the fresh fetch resolves.
 *
 * An empty payload leaves the current mapping in place. With no mapping
 * at all, `getPlatformName` returns its input verbatim, which is the
 * intended degraded mode: raw `id_platform` codes are honest, fabricated
 * names would not be.
 *
 * @param {{
 *   entries: Array<{platform:string,id_platform:string,country:string}>,
 *   colors: Record<string,string>,
 * }} payload
 */
export const applyPlatformMetadata = (payload) => {
  if (!payload || !Array.isArray(payload.entries) || payload.entries.length === 0) {
    return;
  }
  entries = payload.entries.slice();
  colors = { ...(payload.colors || {}) };
  if (!colors.DEFAULT) colors.DEFAULT = "#8B8B8B";
  warnedUnknownInputs.clear();
  version += 1;
  listeners.forEach((listener) => listener());
};

/**
 * `useSyncExternalStore` source for the mapping's version. Read it through
 * `usePlatformMetadataVersion` rather than directly.
 */
export const subscribePlatformMetadata = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getPlatformMetadataVersion = () => version;

const normalize = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, "");

/**
 * Find the platform entry that matches a (input, country?) tuple.
 *
 * `input` is matched against three forms simultaneously:
 *   1. id_platform code ("UBE")
 *   2. display name ("Uber Eats")
 *   3. db_name — the lowercased CH form edge functions emit ("ubereats",
 *      "pyszne", "skip"). Required because some overrides ADD characters
 *      ("Pyszne.pl", "Skip The Dishes") so display normalization alone
 *      can't round-trip back to the DB form.
 *
 * When `country` is provided, restricts to entries from that country —
 * required to disambiguate names that span multiple codes ("justeat" is
 * JSE in UK and TKW in DK; "shopeefood" is SPF in TH/ID/MY and SPV in VN).
 *
 * Returns the matching entry or undefined.
 */
const findEntry = (input, country = null) => {
  if (input == null || input === "") return undefined;
  const raw = String(input);
  const upper = raw.toUpperCase();
  const key = normalize(raw);
  return entries.find(
    (e) =>
      (!country || e.country === country) &&
      (e.id_platform === raw ||
        e.id_platform === upper ||
        normalize(e.platform) === key ||
        normalize(e.db_name) === key)
  );
};

/**
 * Resolve a platform code OR name to its canonical display name.
 *
 * @param {string} input - id_platform code (e.g. "UBE") or platform
 *   name (e.g. "Uber Eats", "ubereats"). Display names from the API
 *   are returned verbatim when matched.
 * @param {string|null} [country] - optional. Required when the same
 *   code or name spans multiple sub-brands across countries (e.g.
 *   TKW renders as "Lieferando" in DE, "Pyszne.pl" in PL; "justeat"
 *   means JSE in UK, TKW in DK).
 * @returns {string} display name, or the input verbatim if unresolvable.
 */
export const getPlatformName = (input, country = null) => {
  const entry = findEntry(input, country);
  if (entry) return entry.platform;
  // No country → try without restriction; if multiple entries match by
  // code or normalized name AND they all share the same display, return
  // it. If they disagree, return the input verbatim so the caller sees
  // the missing-country mistake instead of a misleading label.
  if (country) {
    const fallback = findEntry(input);
    if (fallback) return fallback.platform;
  }
  return input;
};

/**
 * Resolve a platform code OR name to its brand color.
 *
 * @param {string} input - id_platform code or platform name
 * @param {string|null} [country] - optional country hint. Useful when
 *   the input is a name that spans multiple id_platforms with
 *   different colors (none today, but future-proofs the call).
 * @returns {string} hex color, or DEFAULT if unresolvable.
 */
export const getPlatformColor = (input, country = null) => {
  // Direct code lookup is the cheap path — covers the common DashONE
  // "I have an id_platform code" case without iterating entries.
  if (typeof input === "string") {
    if (colors[input]) return colors[input];
    const upper = input.toUpperCase();
    if (colors[upper]) return colors[upper];
  }
  // Otherwise resolve via entries (handles names + country routing).
  const entry = findEntry(input, country);
  if (entry && colors[entry.id_platform]) return colors[entry.id_platform];

  if (input && !warnedUnknownInputs.has(input)) {
    warnedUnknownInputs.add(input);
    console.warn(
      `[platformMapping] No color for platform "${input}" — falling back to DEFAULT. Add it in apps/api/src/data/platform_metadata.py.`
    );
  }
  return colors.DEFAULT;
};

/**
 * Resolve a platform code OR name to its 3-letter id_platform code.
 *
 * @param {string} input - id_platform code or platform name
 * @param {string|null} [country] - required to disambiguate names that
 *   span multiple codes (e.g. "justeat" → JSE in UK, TKW in DK).
 * @returns {string|null} id_platform code, or null if unresolvable.
 */
export const getPlatformCode = (input, country = null) => {
  const entry = findEntry(input, country);
  return entry ? entry.id_platform : null;
};

/**
 * Read-only snapshot of the current entries list. Returned as a fresh
 * array so callers can iterate without affecting the in-memory state.
 */
export const getPlatformEntries = () => entries.slice();

/**
 * Resolve a platform code, database name, or display name to the logo the
 * metadata names for that country's entry. The files live in
 * `public/platforms`.
 *
 * @param {string} input - e.g. "ubereats", "Uber Eats", "UBE"
 * @param {string|null} [country] - picks the country's entry, so a shared
 *   code shows its local brand (TKW is Lieferando in DE, Pyszne.pl in PL)
 * @returns {string|null} image URL, or null when the entry has no logo
 */
export const getPlatformLogo = (input, country = null) => {
  const entry = findEntry(input, country);
  return typeof entry?.logo === "string" && entry.logo ? entry.logo : null;
};
